import { Clock, AlertCircle, CheckCircle, Mail, ChevronRight } from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { Link } from 'react-router-dom';

const STATUS_CONFIG = {
  draft: {
    icon: AlertCircle,
    color: 'text-zinc-400',
    bg: 'bg-zinc-800 border-zinc-700',
    label: 'Draft — Not Submitted',
    message: 'Your application has not been submitted yet. Complete and submit your application to begin the review process.',
    action: { label: 'Complete Application', href: '/dealer-signup' },
  },
  submitted: {
    icon: Clock,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10 border-yellow-500/20',
    label: 'Under Review',
    message: 'Your application has been submitted and is pending review. We\'ll reach out within 2-3 business days.',
    action: null,
  },
  under_review: {
    icon: Clock,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10 border-blue-500/20',
    label: 'In Review',
    message: 'Your application is currently being reviewed by our team. You\'ll receive an email once a decision has been made.',
    action: null,
  },
  info_requested: {
    icon: AlertCircle,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10 border-orange-500/20',
    label: 'Additional Information Required',
    message: 'We need additional information to process your application.',
    action: null,
  },
  rejected: {
    icon: AlertCircle,
    color: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/20',
    label: 'Application Not Approved',
    message: 'Your dealer application was not approved at this time. Please contact us if you have questions.',
    action: { label: 'Contact Us', href: '/contact' },
  },
};

export default function DealerPending({ dealer }) {
  const { logout } = useDealerAuth();
  const cfg = STATUS_CONFIG[dealer.status] || STATUS_CONFIG.submitted;
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center px-4" data-testid="dealer-pending-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-[#111111] border-b border-white/10 flex items-center justify-between px-6 z-10">
        <div>
          <span className="font-bebas text-lg text-white tracking-widest">WITT MACHINE</span>
          <span className="font-inter text-[9px] text-orange-500 tracking-widest uppercase ml-2">Dealer Portal</span>
        </div>
        <button onClick={logout} className="font-inter text-xs text-zinc-500 hover:text-white transition-colors">
          Sign Out
        </button>
      </div>

      <div className="max-w-lg w-full mt-14">
        {/* Status card */}
        <div className={`border p-8 mb-4 ${cfg.bg}`}>
          <div className="flex items-center gap-3 mb-4">
            <Icon size={24} className={cfg.color} />
            <div>
              <h2 data-testid="pending-status-label" className={`font-bebas text-2xl tracking-wider ${cfg.color}`}>
                {cfg.label}
              </h2>
              <p className="font-inter text-xs text-zinc-500">
                Application for {dealer.business_name}
              </p>
            </div>
          </div>
          <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-4">{cfg.message}</p>

          {dealer.admin_note && (
            <div className="bg-black/30 border border-white/10 p-3 mb-4">
              <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1">Note from Witt Machine:</p>
              <p className="font-inter text-sm text-white">{dealer.admin_note}</p>
            </div>
          )}

          {cfg.action && (
            <Link
              to={cfg.action.href}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-sm tracking-widest px-5 py-2.5 transition-colors"
            >
              {cfg.action.label} <ChevronRight size={14} />
            </Link>
          )}
        </div>

        {/* Application info */}
        <div className="bg-[#111111] border border-white/10 p-6">
          <h3 className="font-bebas text-lg text-white tracking-wider mb-4">YOUR APPLICATION</h3>
          <div className="space-y-2">
            {[
              { label: 'Business', value: dealer.business_name },
              { label: 'Contact', value: `${dealer.first_name} ${dealer.last_name}` },
              { label: 'Email', value: dealer.email },
              { label: 'FFL #', value: dealer.ffl_number || 'Not provided' },
              { label: 'Documents', value: `${dealer.documents?.length || 0} uploaded` },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="font-inter text-xs text-zinc-500 uppercase tracking-wider">{item.label}</span>
                <span className="font-inter text-sm text-white">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 text-center">
          <a href="mailto:contact@wittmachine.com" className="flex items-center justify-center gap-2 font-inter text-sm text-zinc-500 hover:text-orange-500 transition-colors">
            <Mail size={14} />
            Questions? Contact us
          </a>
        </div>
      </div>
    </div>
  );
}
